import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { NavigationState } from './types';

// Basic selectors
export const selectNavigationState = (state: RootState): NavigationState =>
  state.navigation;

export const selectCurrentPath = (state: RootState): string =>
  state.navigation.currentPath;

export const selectPreviousPath = (state: RootState): string =>
  state.navigation.previousPath;

export const selectMobileMenuOpen = (state: RootState): boolean =>
  state.navigation.mobileMenuOpen;

export const selectActiveStudio = (state: RootState) =>
  state.navigation.activeStudio;

export const selectNavigationHistory = (state: RootState) =>
  state.navigation.navigationHistory;

// Memoized selectors for computed values
export const selectRecentPaths = createSelector(
  [selectNavigationHistory],
  history => {
    // Get unique paths from recent history (last 10 unique paths)
    const uniquePaths = new Set<string>();
    const recentPaths: string[] = [];

    history.forEach(item => {
      if (!uniquePaths.has(item.path) && recentPaths.length < 10) {
        uniquePaths.add(item.path);
        recentPaths.push(item.path);
      }
    });

    return recentPaths;
  }
);

export const selectBreadcrumbs = createSelector(
  [selectCurrentPath, selectActiveStudio],
  (currentPath, activeStudio) => {
    const breadcrumbs = [];

    // Always start with Home
    breadcrumbs.push({ label: 'Home', path: '/' });

    // Split path and create breadcrumbs
    const pathSegments = currentPath.split('/').filter(segment => segment);

    let currentBreadcrumbPath = '';
    pathSegments.forEach((segment: string) => {
      currentBreadcrumbPath += `/${segment}`;

      // Create human-readable labels
      let label = segment;
      switch (segment) {
        case 'dashboard':
          label = 'Dashboard';
          break;
        case 'studios':
          label = 'Studios';
          break;
        case 'billing':
          label = 'Billing';
          break;
        case 'settings':
          label = 'Settings';
          break;
        case 'profile':
          label = 'Profile';
          break;
        case 'contact':
          label = 'Support';
          break;
        default:
          // Capitalize first letter and replace hyphens with spaces
          label =
            segment.charAt(0).toUpperCase() +
            segment.slice(1).replace(/-/g, ' ');
      }

      breadcrumbs.push({ label, path: currentBreadcrumbPath });
    });

    // Add studio context if available and relevant
    if (
      activeStudio &&
      (currentPath.includes('/studios/') || currentPath.includes('/dashboard'))
    ) {
      // Insert studio name after Studios in breadcrumb
      const studioIndex = breadcrumbs.findIndex(
        crumb => crumb.label === 'Studios'
      );
      if (studioIndex !== -1 && studioIndex < breadcrumbs.length - 1) {
        breadcrumbs.splice(studioIndex + 1, 0, {
          label: activeStudio.name,
          path: `/studios/${activeStudio.id}`,
        });
      }
    }

    return breadcrumbs;
  }
);

export const selectIsNavigationActive = createSelector(
  [selectMobileMenuOpen],
  mobileMenuOpen => mobileMenuOpen
);

export const selectNavigationStats = createSelector(
  [selectNavigationHistory],
  history => {
    const totalNavigations = history.length;
    const uniquePaths = new Set(history.map(item => item.path)).size;
    const averageSessionLength =
      history.length > 0
        ? (Date.now() - Math.min(...history.map(item => item.timestamp))) /
          (1000 * 60) // minutes
        : 0;

    return {
      totalNavigations,
      uniquePaths,
      averageSessionLength: Math.round(averageSessionLength),
    };
  }
);

// Helper selector to check if current path matches a pattern
export const createPathMatcher = (pattern: string | RegExp) =>
  createSelector([selectCurrentPath], currentPath => {
    if (typeof pattern === 'string') {
      return currentPath === pattern || currentPath.startsWith(pattern);
    }
    return pattern.test(currentPath);
  });

// Common path matchers
export const selectIsOnDashboard = createPathMatcher('/dashboard');
export const selectIsOnStudios = createPathMatcher('/studios');
export const selectIsOnBilling = createPathMatcher('/billing');
export const selectIsOnSettings = createPathMatcher('/settings');
export const selectIsOnProfile = createPathMatcher('/profile');
